/**
 * Utility functions for date formatting and manipulation
 */

/**
 * Format a date to a readable string in local format
 * @param {string|Date} date - The date to format
 * @param {boolean} includeTime - Whether to include time in the output
 * @returns {string} Formatted date string
 */
export const formatDate = (date, includeTime = false) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(includeTime && {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    };
    
    return dateObj.toLocaleDateString('en-IN', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error';
  }
};

/**
 * Format a date that may arrive in Tally's compact YYYYMMDD form
 * (e.g. "20251226"), plus ISO / DD-MM-YYYY strings. Returns DD/MM/YYYY.
 * @param {string|Date} value - The date to format
 * @returns {string} Formatted date or the original string when unparseable
 */
export const formatTallyDate = value => {
  if (!value) return '';
  const s = String(value).trim();
  if (/^\d{8}$/.test(s)) {
    return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return `${String(d.getDate()).padStart(2, '0')}/${String(
    d.getMonth() + 1,
  ).padStart(2, '0')}/${d.getFullYear()}`;
};

/**
 * Calculate the difference between two dates in days
 * @param {string|Date} startDate - The start date
 * @param {string|Date} endDate - The end date
 * @returns {number} Number of days between the dates
 */
export const daysBetween = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Set to beginning of day
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  // Calculate difference in milliseconds
  const diffTime = Math.abs(end - start);
  
  // Convert to days
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get the first day of the current month
 * @returns {Date} First day of current month
 */
export const getFirstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

/**
 * Get the last day of the current month
 * @returns {Date} Last day of current month
 */
export const getLastDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
};

/**
 * Format a date as YYYY-MM-DD for input[type="date"]
 * @param {Date|string} date - Date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateForInput = (date = new Date()) => {
  const d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
};

/**
 * Get a date from X days ago
 * @param {number} days - Number of days to go back
 * @returns {Date} Date from X days ago
 */
export const getDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

/**
 * Get the start and end dates of the current month
 * @returns {Object} Object with startDate and endDate as formatted strings (YYYY-MM-DD)
 */
export const getCurrentMonthDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);
  
  return {
    startDate: formatDateForInput(startDate),
    endDate: formatDateForInput(endDate)
  };
};

/**
 * Get the start and end dates of the current month for input fields
 * @returns {Object} Object with startDate and endDate as formatted strings
 */
export const getCurrentMonthDateInputs = () => {
  return getCurrentMonthDates();
};

/**
 * Build fiscal quarter id string.
 * @param {number} fiscalYearStart - Fiscal year start year (e.g. 2026 for FY 2026-27)
 * @param {number} quarter - Quarter number 1..4
 * @returns {string}
 */
export const buildFiscalQuarterId = (fiscalYearStart, quarter) => {
  return `FY${fiscalYearStart}-Q${quarter}`;
};

/**
 * Parse fiscal quarter id.
 * @param {string} quarterId - Expected format FY2026-Q1
 * @returns {{ fiscalYearStart: number, quarter: number } | null}
 */
export const parseFiscalQuarterId = (quarterId) => {
  if (!quarterId || typeof quarterId !== 'string') return null;

  const match = /^FY(\d{4})-Q([1-4])$/.exec(quarterId.trim());
  if (!match) return null;

  return {
    fiscalYearStart: Number(match[1]),
    quarter: Number(match[2])
  };
};

/**
 * Get fiscal quarter metadata from a date using India fiscal year (Apr-Mar).
 * @param {Date|string} date
 * @returns {{
 *   id: string,
 *   label: string,
 *   fiscalYearStart: number,
 *   fiscalYearEndShort: string,
 *   quarter: number,
 *   startDate: string,
 *   endDate: string,
 * }}
 */
export const getFiscalQuarterMeta = (date = new Date()) => {
  const d = new Date(date);
  const month = d.getMonth(); // 0-based
  const year = d.getFullYear();

  const fiscalYearStart = month >= 3 ? year : year - 1;
  const fiscalYearEnd = fiscalYearStart + 1;

  let quarter = 4;
  if (month >= 3 && month <= 5) quarter = 1;
  else if (month >= 6 && month <= 8) quarter = 2;
  else if (month >= 9 && month <= 11) quarter = 3;

  const startMonthByQuarter = { 1: 3, 2: 6, 3: 9, 4: 0 };
  const endMonthByQuarter = { 1: 5, 2: 8, 3: 11, 4: 2 };

  const startYear = quarter === 4 ? fiscalYearEnd : fiscalYearStart;
  const endYear = quarter === 4 ? fiscalYearEnd : fiscalYearStart;

  const quarterStart = new Date(startYear, startMonthByQuarter[quarter], 1);
  const quarterEnd = new Date(endYear, endMonthByQuarter[quarter] + 1, 0);

  return {
    id: buildFiscalQuarterId(fiscalYearStart, quarter),
    label: `FY ${fiscalYearStart}-${String(fiscalYearEnd).slice(-2)} Q${quarter}`,
    fiscalYearStart,
    fiscalYearEndShort: String(fiscalYearEnd).slice(-2),
    quarter,
    startDate: formatDateForInput(quarterStart),
    endDate: formatDateForInput(quarterEnd)
  };
};

/**
 * Build quarter metadata from fiscal year and quarter.
 * @param {number} fiscalYearStart
 * @param {number} quarter
 * @returns {ReturnType<typeof getFiscalQuarterMeta> | null}
 */
export const getFiscalQuarterMetaFromKey = (fiscalYearStart, quarter) => {
  if (!Number.isInteger(fiscalYearStart) || !Number.isInteger(quarter) || quarter < 1 || quarter > 4) {
    return null;
  }

  const anchorDate =
    quarter === 1 ? new Date(fiscalYearStart, 3, 1) :
    quarter === 2 ? new Date(fiscalYearStart, 6, 1) :
    quarter === 3 ? new Date(fiscalYearStart, 9, 1) :
    new Date(fiscalYearStart + 1, 0, 1);

  return getFiscalQuarterMeta(anchorDate);
};

/**
 * Return fiscal quarter options around an anchor date.
 * @param {number} count - Number of quarters to return
 * @param {Date|string} anchorDate
 * @returns {Array<ReturnType<typeof getFiscalQuarterMeta>>}
 */
export const getFiscalQuarterOptions = (count = 12, anchorDate = new Date()) => {
  const safeCount = Math.max(1, count);
  const options = [];

  let current = getFiscalQuarterMeta(anchorDate);

  for (let i = 0; i < safeCount; i += 1) {
    options.push(current);

    const previousQuarter = current.quarter === 1 ? 4 : current.quarter - 1;
    const previousFy = current.quarter === 1 ? current.fiscalYearStart - 1 : current.fiscalYearStart;
    current = getFiscalQuarterMetaFromKey(previousFy, previousQuarter);
  }

  return options;
};

/**
 * Get all quarters inclusively between start and end quarter ids.
 * @param {string} startQuarterId
 * @param {string} endQuarterId
 * @returns {Array<ReturnType<typeof getFiscalQuarterMeta>>}
 */
export const getFiscalQuartersInRange = (startQuarterId, endQuarterId) => {
  const start = parseFiscalQuarterId(startQuarterId);
  const end = parseFiscalQuarterId(endQuarterId);
  if (!start || !end) return [];

  const rank = (fy, q) => fy * 10 + q;

  let left = { ...start };
  let right = { ...end };

  if (rank(left.fiscalYearStart, left.quarter) > rank(right.fiscalYearStart, right.quarter)) {
    left = { ...end };
    right = { ...start };
  }

  const quarters = [];
  let cursor = { ...left };

  while (rank(cursor.fiscalYearStart, cursor.quarter) <= rank(right.fiscalYearStart, right.quarter)) {
    const meta = getFiscalQuarterMetaFromKey(cursor.fiscalYearStart, cursor.quarter);
    if (meta) quarters.push(meta);

    const nextQuarter = cursor.quarter === 4 ? 1 : cursor.quarter + 1;
    const nextFy = cursor.quarter === 4 ? cursor.fiscalYearStart + 1 : cursor.fiscalYearStart;
    cursor = { fiscalYearStart: nextFy, quarter: nextQuarter };
  }

  return quarters;
};

/**
 * Convert fiscal quarter range to concrete date range.
 * @param {string} startQuarterId
 * @param {string} endQuarterId
 * @returns {{ startDate: string, endDate: string, quarters: Array<ReturnType<typeof getFiscalQuarterMeta>> } | null}
 */
export const getDateRangeFromFiscalQuarterRange = (startQuarterId, endQuarterId) => {
  const quarters = getFiscalQuartersInRange(startQuarterId, endQuarterId);
  if (quarters.length === 0) return null;

  return {
    startDate: quarters[0].startDate,
    endDate: quarters[quarters.length - 1].endDate,
    quarters
  };
};
